import { useState, FormEvent, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { useAuth } from '../hooks/useAuth';
import { updateMyProfile, uploadAvatar, markProfileComplete } from '../api/users';

const BIO_MAX = 500;

export default function CompleteSignupPage() {
    const navigate = useNavigate();
    const { user, loading: authLoading, refresh } = useAuth();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [firstName, setFirstName] = useState(user?.firstName ?? '');
    const [lastName, setLastName] = useState(user?.lastName ?? '');
    const [bio, setBio] = useState('');
    const [avatarFile, setAvatarFile] = useState<File | null>(null);
    const [avatarPreview, setAvatarPreview] = useState<string | null>(user?.profileImage ?? null);
    const [errors, setErrors] = useState<string[]>([]);
    const [warning, setWarning] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    if (authLoading) return null;
    if (!user) return <Navigate to="/login" replace />;
    if (user.profileComplete) return <Navigate to="/available-badges" replace />;

    const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setAvatarFile(file);
        setAvatarPreview(URL.createObjectURL(file));
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        const trimmedFirst = firstName.trim();
        const trimmedLast = lastName.trim();
        const trimmedBio = bio.trim();
        const validation: string[] = [];
        if (!trimmedFirst) validation.push('First name is required.');
        if (!trimmedLast) validation.push('Last name is required.');
        if (trimmedBio.length > BIO_MAX) validation.push(`Bio must be ${BIO_MAX} characters or fewer.`);
        if (validation.length > 0) {
            setErrors(validation);
            return;
        }

        setSaving(true);
        setErrors([]);
        setWarning(null);

        if (avatarFile) {
            const avatarRes = await uploadAvatar(avatarFile);
            if (!avatarRes.success) {
                setWarning(avatarRes.message ?? 'Avatar upload failed; profile saved without it.');
            }
        }

        const profileRes = await updateMyProfile({
            firstName: trimmedFirst,
            lastName: trimmedLast,
            bio: trimmedBio,
        });
        if (!profileRes.success) {
            setSaving(false);
            setErrors(profileRes.errors ?? [profileRes.message ?? 'Could not save your profile.']);
            return;
        }

        const completeRes = await markProfileComplete();
        if (!completeRes.success) {
            setSaving(false);
            setErrors(completeRes.errors ?? [completeRes.message ?? 'Could not finalize your profile.']);
            return;
        }

        await refresh();
        navigate('/available-badges');
    };

    return (
        <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center" bgcolor="background.default">
            <Card sx={{ width: 480, border: 'none' }}>
                <CardContent sx={{ p: 4 }}>
                    <Box textAlign="center" mb={3}>
                        <WorkspacePremiumIcon sx={{ fontSize: 48, color: 'accent.main' }} />
                        <Typography variant="h5" fontWeight={700} mt={1}>Finish your profile</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            A few details so others can recognize you when you earn badges.
                        </Typography>
                    </Box>

                    {errors.length > 0 && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {errors.map((err, i) => <div key={i}>{err}</div>)}
                        </Alert>
                    )}
                    {warning && (
                        <Alert severity="warning" sx={{ mb: 2 }}>{warning}</Alert>
                    )}

                    <Box component="form" onSubmit={handleSubmit} display="flex" flexDirection="column" gap={2}>
                        <Box display="flex" alignItems="center" gap={2}>
                            <Avatar src={avatarPreview ?? undefined} sx={{ width: 64, height: 64 }}>
                                {firstName.charAt(0).toUpperCase() || '?'}
                            </Avatar>
                            <Box>
                                <Button
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={saving}
                                >
                                    {avatarFile ? 'Change photo' : 'Upload photo'}
                                </Button>
                                <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                    Optional · PNG or JPG up to 5 MB
                                </Typography>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={handleAvatarChange}
                                />
                            </Box>
                        </Box>

                        <Grid container spacing={1.5}>
                            <Grid item xs={6}>
                                <TextField
                                    id="firstName"
                                    label="First Name"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    required
                                    autoComplete="given-name"
                                    size="small"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    id="lastName"
                                    label="Last Name"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    required
                                    autoComplete="family-name"
                                    size="small"
                                    fullWidth
                                />
                            </Grid>
                        </Grid>

                        <TextField
                            id="bio"
                            label="Short bio (optional)"
                            value={bio}
                            onChange={(e) => setBio(e.target.value)}
                            multiline
                            minRows={3}
                            maxRows={6}
                            inputProps={{ maxLength: BIO_MAX }}
                            helperText={`${bio.length} / ${BIO_MAX}`}
                            fullWidth
                        />

                        <Button type="submit" variant="contained" fullWidth disabled={saving}>
                            {saving ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
                            Continue
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}
